// packages/ui/src/utils/describeAuthError.ts
//
// Shared helper mapping auth error codes -> user-friendly Vietnamese messages.
// Wave 12 Task 77 — extracts duplicate `describeError` from 3 SPA LoginPages
// per CONTEXT.md (cleanups bundle, MISS-3 admin consistency).
//
// All 3 portals (admin/merchant/vendor) import this single source to ensure:
//   - Consistent error messaging across portals
//   - AUTH_ACCOUNT_LOCKED case present (admin previously missing — MISS-3)
//   - Single point of update when adding new auth error codes

export interface AuthErrorContext {
  /** Optional unix-ms timestamp when account unlocks (for AUTH_ACCOUNT_LOCKED). */
  lockedUntil?: number | null;
  /** Optional portal hint for portal-specific copy variants (admin vs merchant). */
  portal?: 'admin' | 'merchant' | 'vendor';
}

/**
 * Map auth error code -> Vietnamese user-facing message.
 *
 * @param code - Server error code (e.g., 'AUTH_INVALID_CREDENTIALS')
 * @param ctx - Optional context for lockout countdown / portal-specific copy
 * @returns Vietnamese message ready for toast/banner display
 */
export function describeAuthError(code: string, ctx?: AuthErrorContext): string {
  switch (code) {
    case 'AUTH_INVALID_CREDENTIALS':
      return 'Email hoặc mật khẩu không đúng.';

    case 'AUTH_RECAPTCHA_FAILED':
      return 'reCAPTCHA (cơ chế chống bot) thất bại — vui lòng thử lại.';

    case 'AUTH_EMAIL_UNVERIFIED':
      // Admin: invite-only, contact superadmin. Merchant/vendor: self-service email check.
      if (ctx?.portal === 'admin') {
        return 'Email chưa xác thực. Liên hệ Superadmin.';
      }
      return 'Email chưa xác thực. Kiểm tra hộp thư để hoàn tất.';

    case 'AUTH_2FA_REQUIRED':
      return 'Cần xác thực 2 lớp (2FA) — nhập mã từ ứng dụng Authenticator.';

    case 'AUTH_ACCOUNT_LOCKED': {
      // MISS-3 fix — admin LoginPage previously missing this case.
      // Render countdown if lockedUntil context provided.
      if (ctx?.lockedUntil) {
        const remainingMs = Math.max(0, ctx.lockedUntil - Date.now());
        const mm = String(Math.floor(remainingMs / 60000)).padStart(2, '0');
        const ss = String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, '0');
        return `Tài khoản tạm khoá do nhiều lần đăng nhập sai. Mở khoá sau ${mm}:${ss}.`;
      }
      return 'Tài khoản tạm khoá do nhiều lần đăng nhập sai. Vui lòng đợi vài phút và thử lại.';
    }

    case 'AUTH_PENDING_APPROVAL':
    // AUTH_VENDOR_PENDING_APPROVAL: login handler returns this 403 code when a
    // self-registered vendor is held at status=pending_approval. Without this
    // case the code fell through to default and (worse) the openapi-fetch
    // error-body drop made readErrorCode guess 403 → AUTH_RECAPTCHA_FAILED.
    case 'AUTH_VENDOR_PENDING_APPROVAL':
      // Vendor-specific: application submitted but not yet approved.
      return 'Tài khoản vendor đang chờ admin duyệt.';

    case 'AUTH_RATE_LIMIT_EXCEEDED':
    case 'RATE_LIMIT':
      return 'Quá nhiều lần thử — vui lòng đợi 15 phút.';

    case 'VALIDATION_FAILED':
      return 'Dữ liệu nhập chưa hợp lệ.';

    case 'AUTH_REQUIRED':
      return 'Vui lòng đăng nhập để tiếp tục.';

    case 'FORBIDDEN':
    case 'AUTH_PERMISSION_DENIED':
      return 'Tài khoản không có quyền truy cập.';

    default:
      return 'Đăng nhập thất bại. Vui lòng thử lại.';
  }
}
